<template>
    <div class="form-type-0">
        <form @submit.prevent="submitContactForm" @input="onInputChange" ref="form" novalidate>
            <CustomInput
                v-model="name"
                inputKey="user_name"
                type="text"
                ref="name"
                autocomplete="name"
                required
            />

            <CustomInput
                v-model="email"
                inputKey="email"
                type="email"
                ref="email"
                autocomplete="email"
                required
            />

            <CustomInput
                v-model="phone"
                inputKey="phone"
                type="text"
                ref="phone"
                autocomplete="phone"
            />

            <div class="form-divider"></div>

            <CustomInput
                v-model="company_name"
                inputKey="invoice_name"
                type="text"
                ref="company_name"
                autocomplete="organization"
                required
            />

            <CustomInput
                v-model="address"
                inputKey="invoice_address"
                type="text"
                ref="address"
                autocomplete="street-address"
                required
            />

            <CustomInput
                v-model="postal_code"
                inputKey="invoice_postal_code"
                type="text"
                ref="postal_code"
                autocomplete="postal-code"
                required
            />

            <CustomInput
                v-model="city"
                inputKey="invoice_city"
                type="text"
                ref="city"
                autocomplete="address-level2"
                required
            />

            <CustomTextarea
                v-model="comment"
                inputKey="invoice_comment"
                type="comment"
                ref="comment"
            />

            <div class="form-divider"></div>

            <SubscriptionCoupon :coupon="coupon" inputKey="coupon_builder" @newCoupon="datasetStore.setCoupon" cssClass="coupon-type-1" />

            <div class="form-divider"></div>

            <CustomToggle
                v-model="consent"
                inputKey="options_checkout_dataset"
                ref="options-checkout-dataset"
                :options="consentOptions"
            />

            <button type="submit" :disabled="loading">
                <span v-if="loading" class="loading-spinner-type-1 primary"></span>
                <span v-else>{{ $t('forms.dataset_order_form.button') }}</span>
            </button>
            <p v-if="formError" class="form-error">{{ $t('forms.dataset_order_form.error') }}</p>
        </form>
    </div>
</template>

<script>
    import { mapActions } from 'pinia';
    import { useUserStore } from '@/stores/user';
    import { useDatasetStore } from '@/stores/dataset';
    import formMixin from '@/mixins/formMixin';
    import CustomInput from '@/components/elements/inputs/CustomInput';
    import CustomTextarea from '@/components/elements/inputs/CustomTextarea';
    import CustomToggle from '@/components/elements/inputs/CustomToggle';
    import SubscriptionCoupon from '@/components/elements/order/SubscriptionCoupon';
    import { gaBuilderFormSubmit } from '@/utils/analytics/builder';

    export default {
        mixins: [formMixin],
        emits: ['form_submission_successful'],
        components: {
            CustomInput,
            CustomTextarea,
            CustomToggle,
            SubscriptionCoupon
        },
        props: {
            query: {
                type: String,
                required: false
            }
        },
        data() {
            return {
                name: '',
                email: '',
                phone: '',
                company_name: '',
                address: '',
                postal_code: '',
                city: '',
                comment: '',
                consent: ['tc', 'l'],
                consentOptions:
                    [
                        { value: 'tc', text: 'fields.options_checkout_dataset.options.tc', required: true },
                        { value: 'l', text: 'fields.options_checkout_dataset.options.l', required: true },
                        { value: 'news', text: 'fields.options_checkout_dataset.options.news', required: false },
                    ],
            };
        },
        computed: {
            userStore() {
                return useUserStore();
            },
            datasetStore() {
                return useDatasetStore();
            },
            coupon() {
                return this.datasetStore.coupon;
            }
        },
        methods: {
            ...mapActions(useDatasetStore, ['setCoupon']),
            async submitContactForm() {
                const form = this.$refs.form;
                if(form.checkValidity()) {
                    this.loading = true;

                    let formData = {
                        name: this.name,
                        email: this.email,
                        phone: this.phone,
                        company: this.company_name,
                        address: this.address,
                        postal_code: this.postal_code,
                        city: this.city,
                        comment: this.comment,
                    };

                    if(this.query && this.query.length > 0) {
                        formData.query = this.query;
                    }

                    const success = await this.datasetStore.createOrder(formData);
                    this.loading = false;
                    
                    if (success && this.datasetStore.order.checkout_url) {
                        if(!this.userStore.isAuthenticated) { // If user is not authenticated, save the user data locally
                            this.userStore.setUser({
                                name: this.name,
                                email: this.email,
                                phone: this.phone,
                                company_name: this.company_name,
                                address: this.address,
                                postal_code: this.postal_code,
                                city: this.city,
                            });
                        }

                        form.reset();
                        this.formError = false;
                        this.$emit('form_submission_successful');

                        gaBuilderFormSubmit('checkout', true, this.datasetStore.count || null, this.datasetStore.price || null);
                    } else {
                        this.formError = true;
                        gaBuilderFormSubmit('checkout', false);
                    }
                } else {
                    this.validateAllFields();
                }
            },
            initForm() {
                if(this.userStore.user) {
                    this.name = this.userStore.user.name || '';
                    this.email = this.userStore.user.email || '';
                    this.phone = this.userStore.user.phone || '';
                    this.company_name = this.userStore.user.company_name || '';
                    this.address = this.userStore.user.address || '';
                    this.postal_code = this.userStore.user.postal_code || '';
                    this.city = this.userStore.user.city || '';
                }
            }
        },
        mounted() {
            this.initForm();
            this.datasetStore.refs.orderForm = this;
        },
        unmounted() {
            this.datasetStore.refs.orderForm = null;
        },
    }
</script>